import { ZodSchema, ZodError } from 'zod';

/**
 * Validates frontend environment variables against a Zod schema.
 * Call at app startup / build time. Throws with a clear message on failure.
 *
 * @example
 * ```ts
 * import { envSchema } from './env.schema';
 * import { validateFrontendEnv } from '@monorepo/shared-env';
 *
 * // Vite app
 * validateFrontendEnv(envSchema, import.meta.env, 'hub-consumer');
 *
 * // Next.js app
 * validateFrontendEnv(envSchema, process.env, 'hub-next');
 * ```
 */
export function validateFrontendEnv<T>(
  schema: ZodSchema<T>,
  env: Record<string, unknown>,
  appName?: string,
): T {
  const result = schema.safeParse(env);

  if (!result.success) {
    const formatted = formatZodError(result.error);
    const label = appName ? ` for [${appName}]` : '';
    const message = `\n❌ Environment validation failed${label}:\n\n${formatted}\n`;
    console.error(message);
    throw new Error(message);
  }

  return result.data;
}

function formatZodError(error: ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path.join('.');
      return `  • ${path}: ${issue.message}`;
    })
    .join('\n');
}
